import { Field, getIn, useFormikContext } from "formik";
import { InputT2Group } from "../../../types/modulesInterfaces";
import { FC, useMemo } from "react";
import { selectIsFetchingData } from "@app/features/builder/builderSlice";
import { useAppSelector, useInputNameHandler } from "@app/hooks";
import { useGetInputTypesQuery } from "@hooks/index";
import { TierTwoIcon } from "@utils/customIcons";
import { checkNullorUndefined, roundToDecimals } from "@pages/activityBuilder/modules/inputs/utils";
import SubModuleInputGroup from "../SubModuleInputGroup";
import FormRow from "../formRow/FormRow";
import CustomInput from "../CustomInput";

const TierTwoInput: FC<InputT2Group> = ({ inputName, index, disabled }) => {
  const { values } = useFormikContext();
  const { name } = useInputNameHandler({ inputName, index })

  const entry = getIn(values, name);
  const isFetchingData = useAppSelector(selectIsFetchingData);

  const { data: inputTypes } = useGetInputTypesQuery();
  const inputType = useMemo(() => inputTypes?.find(input => input.id === entry?.input_type), [inputTypes, entry?.input_type]);
  const rowChanged = useMemo<boolean>(
    () =>
      !!entry?.co2_e_emissions_t2 ||
      !!entry?.n2o_emissions_t2 ||
      !!entry?.co2_emissions_t2,
    [entry],
  );

  return (
    <div className="tiertwo-row">
      <TierTwoIcon />
      <div className="d-flex f-column align-items-start pos-relative ps-1 text-start">
        {rowChanged ? <div className="has-changed-dot" /> : null}
        <span className="fs-12">{inputType ? inputType.name : "Please, select Input Type"}</span>
      </div>
      <SubModuleInputGroup
        name=""
        gridTemplateColumns="1fr 1fr 1fr"
        gap="8px"
      >
        <FormRow
          style={{ gridColumn: "1" }}
          topLabel={!inputType?.has_co2_emissions || disabled ? "CO2 Emissions" : (!checkNullorUndefined(entry?.co2_emissions_t2_default) ? [
            `Default = ${roundToDecimals(entry?.co2_emissions_t2_default as number, 3)}`,
            "CO2 Emissions",
          ] : "CO2 Emissions")}
          isFetching={isFetchingData}
        >
          <Field
            name={`entries.${index}.co2_emissions_t2`}
            id={`entries.${index}.co2_emissions_t2`}
            component={CustomInput}
            label={inputType?.has_co2_emissions ? "[unit]" : "/"}
            disabled={!inputType?.has_co2_emissions || disabled}
            type="number"
            textend
            classes={entry?.co2_emissions_t2 ? "tiertwo-input-changed" : ""}
          />
        </FormRow>
        <FormRow
          style={{ gridColumn: "2" }}
          topLabel={!inputType?.has_n2o_emissions || disabled ? "N2O Emissions" : (!checkNullorUndefined(entry?.n2o_emissions_t2_default) ? [
            `Default = ${roundToDecimals(entry?.n2o_emissions_t2_default as number, 3)}`,
            "N2O Emissions",
          ] : "N2O Emissions")}
          isFetching={isFetchingData}
        >
          <Field
            name={`entries.${index}.n2o_emissions_t2`}
            id={`entries.${index}.n2o_emissions_t2`}
            component={CustomInput}
            label={inputType?.has_n2o_emissions ? "[unit]" : "/"}
            disabled={!inputType?.has_n2o_emissions || disabled}
            type="number"
            textend
            classes={entry?.n2o_emissions_t2 ? "tiertwo-input-changed" : ""}
          />
        </FormRow>
        <FormRow
          style={{ gridColumn: "3" }}
          topLabel={!inputType?.has_co2_e_emissions || disabled ? "CO2-e Emissions" : (!checkNullorUndefined(entry?.co2_e_emissions_t2_default) ? [
            `Default = ${roundToDecimals(entry?.co2_e_emissions_t2_default as number, 3)}`,
            "CO2-e Emissions",
          ] : "CO2-e Emissions")}
          isFetching={isFetchingData}
        >
          <Field
            name={`entries.${index}.co2_e_emissions_t2`}
            id={`entries.${index}.co2_e_emissions_t2`}
            component={CustomInput}
            label={inputType?.has_co2_e_emissions ? "[unit]" : "/"}
            disabled={!inputType?.has_co2_e_emissions || disabled}
            type="number"
            textend
            classes={entry?.co2_e_emissions_t2 ? "tiertwo-input-changed" : ""}
          />
        </FormRow>
      </SubModuleInputGroup>
    </div>
  );
}

export default TierTwoInput;